/**
 * useNavigation - Navigation state management hook
 *
 * Features:
 * - Active route tracking
 * - Deep linking support
 * - Scroll position preservation
 * - Navigation history
 */

import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useCallback, useState } from 'react';

const SCROLL_POSITIONS_KEY = 'trueprice:scroll-positions';

interface ScrollPositions {
  [path: string]: number;
}

export function useNavigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrollPositions, setScrollPositions] = useState<ScrollPositions>({});

  // Load scroll positions from sessionStorage
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(SCROLL_POSITIONS_KEY);
      if (stored) {
        setScrollPositions(JSON.parse(stored));
      }
    } catch (error) {
      console.warn('[useNavigation] Failed to load scroll positions:', error);
    }
  }, []);

  // Save scroll position when leaving a page
  useEffect(() => {
    const handleScroll = () => {
      setScrollPositions((prev) => {
        const updated = {
          ...prev,
          [location.pathname]: window.scrollY,
        };

        try {
          sessionStorage.setItem(SCROLL_POSITIONS_KEY, JSON.stringify(updated));
        } catch (error) {
          console.warn('[useNavigation] Failed to save scroll position:', error);
        }

        return updated;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  // Restore scroll position when entering a page
  useEffect(() => {
    const savedPosition = scrollPositions[location.pathname];
    if (savedPosition !== undefined) {
      // Delay scroll restoration to allow content to load
      setTimeout(() => {
        window.scrollTo({ top: savedPosition, behavior: 'instant' });
      }, 0);
    } else {
      // Scroll to top for new pages
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [location.pathname, scrollPositions]);

  // Navigate with scroll reset
  const navigateTo = useCallback(
    (path: string, options?: { replace?: boolean; resetScroll?: boolean }) => {
      if (options?.resetScroll !== false) {
        // Clear saved scroll position for this path
        setScrollPositions((prev) => {
          const updated = { ...prev };
          delete updated[path];

          try {
            sessionStorage.setItem(SCROLL_POSITIONS_KEY, JSON.stringify(updated));
          } catch (error) {
            console.warn('[useNavigation] Failed to clear scroll position:', error);
          }

          return updated;
        });
      }

      navigate(path, { replace: options?.replace });
    },
    [navigate]
  );

  // Check if path is active
  const isActive = useCallback(
    (path: string) => {
      return location.pathname === path;
    },
    [location.pathname]
  );

  // Check if path matches (supports wildcards)
  const matches = useCallback(
    (pattern: string) => {
      if (pattern.endsWith('/*')) {
        const base = pattern.slice(0, -2);
        return location.pathname.startsWith(base);
      }
      return location.pathname === pattern;
    },
    [location.pathname]
  );

  return {
    location,
    navigate: navigateTo,
    isActive,
    matches,
    pathname: location.pathname,
    search: location.search,
    hash: location.hash,
  };
}

/**
 * useMediaQuery - Responsive breakpoint hook
 *
 * Used by AppLayout to switch between Sidebar and BottomTabs
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches;
    }
    return false;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia(query);
    const handler = () => setMatches(mediaQuery.matches);

    // Set initial value
    setMatches(mediaQuery.matches);

    // Listen for changes
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handler);
      return () => mediaQuery.removeEventListener('change', handler);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handler);
      return () => mediaQuery.removeListener(handler);
    }
  }, [query]);

  return matches;
}
